// #region Imports
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { Theme } from "@mui/material";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import Tab from '@mui/material/Tab';
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  ConfirmResponse,
  useConfirmDialog
} from '_shared/_providers/dialogs/confirm/confirm-dialog';
import { useDialog } from '_shared/_providers/dialogs/dialog';
import RowActions from '_shared/_views/tables/row-actions';
import RowLink from '_shared/_views/tables/row-link';
import { MRTNoMemo } from '_shared/_views/tables/table';
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import Guid from '_shared/guids/guid';
import CompanyAvatar from 'marketplace/_views/avatars/company-avatar';
import {
  useDeleteCompanyBookmarkMutation,
  useGetAllCompanyBookmarksQuery,
} from 'marketplace/bookmarks/_api/bookmark-api';
import { PageTab } from 'marketplace/bookmarks/_views/bookmarks-page-tabs';
import BookmarkedCompany from 'marketplace/bookmarks/company-bookmarks/bookmarked-company';
import {
  getCompaniesByIds,
} from 'marketplace/companies/_store/companies-redux-slice';
import { companiesApi } from 'marketplace/companies/_api/company-api';
import Company from 'marketplace/companies/company';
import ViewCompanyProfile from 'marketplace/companies/profiles/_views/view-company-profile';
import {
  createMRTColumnHelper,
  type MRT_TableOptions,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
} from "material-react-table";
import {
  MobileToolbarButtonsContainer,
  ScrollableTabs,
  searchFieldSpacing,
  searchTextFieldProps,
  topToolbarFitsThePage,
  ToolbarButtons,
} from "_shared/_views/tables/table-toolbar";
import { enqueueSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
// #endregion

// #region Styled Components
const ToolbarButtonsContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  display: "flex",
  alignItems: "end",
  flexDirection: "column",
  gap: theme.spacing(1),
}));
const TableTopToolbarContainer = styled("div")(() => ({
  maxWidth: "100%",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  width: "100%",
}));
const LowerTopToolbarContainer = styled("div")(({ theme }) => ({
  minWidth: 0,
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
  display: "flex",
  flexDirection: "row",
  alignItems: "left",
  width: "100%",
}));
const TableTitle = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
}));
const RowTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1.1em",
  fontWeight: "600",
  marginLeft: theme.spacing(1),
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
const NameCellContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
}));
// #endregion

// #region Component Input Props
type CompanyBookmarksProps = {
  activeTab: PageTab;
};

const columnHelper = createMRTColumnHelper<BookmarkedCompany>();

export default function CompanyBookmarks(props: Readonly<CompanyBookmarksProps>) {
  const { activeTab } = props;

  // #region Hooks
  const navigate = useNavigate();
  const confirm = useConfirmDialog();
  const { openDialog } = useDialog();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );

  // Bookmark Query
  const {
    data: bookmarksJson,
    isError: bookmarksError,
    isFetching: bookmarksFetching,
    isLoading: bookmarksLoading,
    refetch: refetchBookmarks,
  } = useGetAllCompanyBookmarksQuery();
  const [deleteCompanyBookmark, { isLoading: isDeleting }] = useDeleteCompanyBookmarkMutation();
  const bookmarks = useMemo(
    () => bookmarksJson?.map((b) => BookmarkedCompany.fromJSON(b)).filter((b): b is BookmarkedCompany => !!b) ?? [],
    [bookmarksJson],
  );

  // All companyIds to fetch
  const bookmarkedCompanyIds = useMemo(
    () => bookmarks.map(b => b.companyId),
    [bookmarks]
  );

  // Company Selectors
  const companies = useSelector(getCompaniesByIds(bookmarkedCompanyIds));
  const companyEntries = useMemo(() => {
    const entries: Record<string, Company> = {};
    for (const company of companies) {
      entries[company.id.value] = company;
    }
    return entries;
  }, [companies]);

  // Calculate missing company IDs
  const missingIds = useMemo(() => {
    return bookmarkedCompanyIds.filter((id) => !companyEntries[id.value]);
  }, [bookmarkedCompanyIds, companyEntries]);

  // Fetch missing companies via RTK Query
  const { isLoading: companiesLoading, isError: companiesError } = companiesApi.useGetCompaniesByIdsQuery(
    { ids: missingIds.map(id => id.value) },
    { skip: !missingIds.length }
  );

  useEffect(() => {
    if (bookmarksError) {
      enqueueSnackbar("Error loading bookmarks. Please try again.", { variant: 'error' });
    }
  }, [bookmarksError]);

  // #region Event Handlers
  async function handleDeleteBookmark(bookmark: BookmarkedCompany) {
    const response = await confirm({
      title: "Delete Bookmark?",
      message: "Do you want to delete?",
      okButtonText: "Delete",
      cancelButtonText: "Cancel",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      await deleteCompanyBookmark({ bookmarkId: bookmark.id.value }).unwrap();
      enqueueSnackbar("Deleted bookmark", { variant: 'success' });
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Couldn't delete bookmark. Please try again", { variant: "error" });
    }
  }

  // Memoized because the name cell's link depends on it, and a fresh function each render would
  // rebuild that cell — and through it the column list — on every render.
  const handleViewProfile = useCallback((profileId?: Guid): void => {
    if (!profileId) return;
    openDialog({
      component: <ViewCompanyProfile companyId={profileId} />,
      titleStyle: {
        paddingBottom: 0
      },
      contentSxProps: {
        display: "flex",
        overflowX: "hidden",
      },
      MuiProps: {
        maxWidth: "lg",
        fullWidth: false,
      },
    });
  }, [openDialog]);
  // #endregion

  // #region Columns
  const renderNameColumn = useCallback((bookmark: BookmarkedCompany) => {
    const id = bookmark.companyId;
    const company = companyEntries[id.value];

    const loadingText = companiesLoading ? "Loading..." : "Unknown Company";
    const name =
      company?.profile?.name ??
      (companiesError ? "Error loading" : loadingText);

    return (
      <NameCellContainer>
        <CompanyAvatar 
          company={company ?? null}
        />
        <RowTitle>
          {/* Only a row that resolved to a company opens one — "Loading...", "Unknown Company" and
              "Error loading" are the cell saying it has nothing to offer, and must not look like
              they do. */}
          <RowLink onClick={company ? () => handleViewProfile(id) : undefined}>{name}</RowLink>
        </RowTitle>
      </NameCellContainer>
    );
  }, [
    companyEntries,
    companiesLoading,
    companiesError,
    handleViewProfile
  ]);

  const columns = useMemo(() => [
    columnHelper.display({
      header: "Name",
      id: "name",
      Cell: ({ row }) => renderNameColumn(row.original)
    }),
    columnHelper.accessor((bookmark) => bookmark.createdDate.format("MM/DD/YY"), {
      header: "Bookmarked On",
      id: "created",
    }),
  ], [renderNameColumn]);
  // #endregion

  // #region Table
  const tableOptions: MRT_TableOptions<BookmarkedCompany> = {
    columns,
    data: bookmarks ?? [],
    enableRowSelection: false,
    enableTableHead: true,
    enableColumnPinning: true,
    enableRowActions: true,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => handleViewProfile(row.original.companyId),
      sx: { cursor: "pointer" },
    }),
    renderToolbarInternalActions: ({ table }) => (
      <>
        {!isMediumDisplaySize && (
          <ToolbarButtonsContainer>
            <ToolbarButtons>
              <GlobalFilterTextField table={table} sx={searchFieldSpacing} />
              <ToggleFiltersButton table={table} />
              <ShowHideColumnsButton table={table} />
              <ToggleFullScreenButton table={table} />
            </ToolbarButtons>
          </ToolbarButtonsContainer>
        )}
      </>
    ),
    renderTopToolbarCustomActions: ({ table }) => {
      return (
        <TableTopToolbarContainer>
          <TableTitle variant="h4">Bookmarks</TableTitle>
          <LowerTopToolbarContainer>
            <ScrollableTabs
              value={activeTab}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              onChange={(_event, newValue) => navigate(`/marketplace/bookmarks/${newValue}`)}>
              <Tab value={PageTab.Individual} label="Individual" />
              <Tab value={PageTab.Team} label="Team" />
              <Tab value={PageTab.Company} label="Company" />
            </ScrollableTabs>
          </LowerTopToolbarContainer>
          {isMediumDisplaySize && (
            <MobileToolbarButtonsContainer>
              <GlobalFilterTextField table={table} />
              <ToolbarButtons>
                <ToggleFiltersButton table={table} />
                <ShowHideColumnsButton table={table} />
                <ToggleFullScreenButton table={table} />
              </ToolbarButtons>
            </MobileToolbarButtonsContainer>
          )}
        </TableTopToolbarContainer>
      );
    },
    getRowId: (row) => row.id?.value ?? row.companyId?.value,
    initialState: {
      showGlobalFilter: true,
      columnPinning: { right: ["mrt-row-actions"] },
    },
    positionGlobalFilter: "none",
    muiSearchTextFieldProps: ({ table }) =>
      searchTextFieldProps({ searches: "company bookmarks", table }),
    renderEmptyRowsFallback: emptyRowsFallback({
      isError: bookmarksError || companiesError,
      isLoading: bookmarksFetching,
      onRetry: () => void refetchBookmarks(),
      subject: "company bookmarks",
    }),
    renderRowActions: ({ row }) => {
      const companyName = companyEntries[row.original.companyId.value]?.profile?.name ?? "company";
      return (
        <RowActions
          ariaLabel={`Actions for ${companyName}`}
          actions={[
            {
              key: "delete",
              label: "Delete Bookmark",
              ariaLabel: `Delete bookmark for ${companyName}`,
              icon: <DeleteIcon />,
              destructive: true,
              onClick: () => {
                void handleDeleteBookmark(row.original);
              },
            },
          ]}
        />
      );
    },
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    manualPagination: true,
    manualSorting: true,
    muiTableContainerProps: {
      sx: {
        flex: 1
      },
    },
    muiTopToolbarProps: { sx: topToolbarFitsThePage },
    muiTablePaperProps: {
      sx: {
        height: "100%",
        display: "flex",
        flexDirection: "column"
      },
    },
    muiToolbarAlertBannerProps: bookmarksError
        ? {
            color: "error",
            children: "Error loading data",
          }
        : undefined,
    // onColumnFiltersChange: setColumnFilters,
    // onGlobalFilterChange: setGlobalFilter,
    // onPaginationChange: setPagination,
    // onSortingChange: setSorting,
    rowCount: bookmarks?.length ?? 0,
    state: {
      // columnFilters,
      // globalFilter,
      isLoading: bookmarksLoading || isDeleting,
      // pagination,
      showAlertBanner: bookmarksError || companiesError,
      showProgressBars: bookmarksLoading || isDeleting,
      // sorting,
    },
  }

  return <MRTNoMemo<BookmarkedCompany> options={tableOptions} />;
}
